import { useQuery } from '@tanstack/react-query';
import { getAccessToken, getAuthReady } from '@/lib/authBootstrap';
import { apiFetch } from '@/lib/http';

interface User {
  id: number;
  email: string;
  username: string;
  role: string;
  name: string;
  roles: string[];
  region?: string;
  isActive: boolean;
  mobileTestModeEnabled?: boolean;
  requirePasswordChange?: boolean;
  accessMode?: 'internal' | 'portal_external';
}

/**
 * Hook to get authenticated user data using Supabase Auth
 */
export function useAuthUser() {
  const { data, isLoading, isFetching, error } = useQuery<User | null>({
    queryKey: ['auth', 'user'],
    queryFn: async () => {
      console.log('[AUTH_SUPABASE] useAuthUser: Checking auth state...');
      
      // Wait for auth to be ready
      const isReady = await getAuthReady();
      if (!isReady) {
        console.log('[AUTH_SUPABASE] useAuthUser: Auth not ready - returning null');
        return null;
      }

      // Get token
      const token = await getAccessToken();
      if (!token) {
        console.log('[AUTH_SUPABASE] useAuthUser: No token available - returning null');
        return null;
      }

      console.log('[AUTH_SUPABASE] useAuthUser: Fetching user data from /api/users/me');
      
      try {
        // Call /api/users/me with Supabase token
        const response = await apiFetch('/api/users/me');
        
        if (!response.ok) {
          if (response.status === 401) {
            console.log('[AUTH_SUPABASE] useAuthUser: Unauthorized - token invalid');
            return null;
          }
          if (response.status === 404) {
            console.log('[AUTH_SUPABASE] useAuthUser: User not linked to Supabase account');
            return null;
          }
          throw new Error(`API request failed: ${response.status}`);
        }

        const result = await response.json();
        console.log('[AUTH_DEBUG] useAuthUser: Raw response from /api/users/me:', result);
        
        // Handle different response formats from duplicate endpoints
        let userData;
        if (result.user) {
          // Format 1: { success: true, user: {...} }
          userData = result.user;
          console.log('[AUTH_DEBUG] useAuthUser: Using wrapped format (result.user)');
        } else if (result.id) {
          // Format 2: {...user data directly...}
          userData = result;
          console.log('[AUTH_DEBUG] useAuthUser: Using direct format (result)');
        } else {
          console.error('[AUTH_DEBUG] useAuthUser: Unexpected response format:', result);
          return null;
        }
        
        console.log('[AUTH_DEBUG] useAuthUser: Final user data:', { 
          id: userData?.id, 
          email: userData?.email,
          role: userData?.role,
          roles: userData?.roles
        });
        
        // Normalize: ensure display fields and temporary role default for missing roles
        const rawRoles = (userData as any).roles;
        const parsedRoles = (() => {
          if (Array.isArray(rawRoles)) return rawRoles as string[];
          if (typeof rawRoles === 'string') {
            try {
              const parsed = JSON.parse(rawRoles);
              if (Array.isArray(parsed)) return parsed as string[];
            } catch { }
          }
          return null;
        })();
        const rolesArr = parsedRoles ?? ((userData as any).role ? [(userData as any).role] : []);
        const hasRole = rolesArr.length > 0;
        const inferredName = (userData as any).name || (userData as any).username || (userData as any).email || 'User';
        const normalized = {
          ...userData,
          name: (userData as any).name ?? inferredName,
          username: (userData as any).username ?? (userData as any).email ?? inferredName,
          role: hasRole ? (userData as any).role : ((userData as any).role ?? 'admin'),
          roles: hasRole ? rolesArr : (rolesArr.length ? rolesArr : ['admin']),
          requirePasswordChange: Boolean((userData as any).requirePasswordChange)
        } as User as unknown as any;
        return normalized as unknown as User;
      } catch (error) {
        console.error('[AUTH_SUPABASE] useAuthUser: Error fetching user:', error);
        return null;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchInterval: false,
    refetchOnReconnect: false,
  });

  // Derive authentication state
  const isAuthenticated = !!data;
  
  return {
    user: data,
    isLoading,
    isFetching,
    isAuthenticated,
    error
  };
}
